import { Button, CheckIcon, EditIcon, EmptyState, Modal } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConditionsDuplication } from '../../../hooks/useConditionsDuplication';
import {
  TeacherConstraints,
  teacherConstraintFields,
  useTeacherConstraints,
} from '../../../hooks/useTeacherConstraints';
import { showSuccessSaveToast, showUserErrorToast } from '../../../utils/toast';
import { ConstraintsItem } from '../../Constraints/ConstraintsItem';
import { DuplicateConditionsModal } from '../../Constraints/DuplicateConditionsModal';
import { TeacherConditionsForm } from '../../Constraints/Form/TeacherConditionsForm';
import { ConditionsControlProps, ConstraintsItemType } from '../../Constraints/types';
import { useTimetableStore } from '../../TimetableGrid/TimetableProvider';
import { useVersionConstraints } from '../hooks/useVersionConstraints';

/*
 * Lehrer-Bedingungen (P2: L4 Std/Tag, L1 Tage/Woche, L10 ?-Positionen, L8 max. zusammenhängend). Schreibt den
 * `constraints`-Blob auf die Teacher↔Version-Kante, den der Timefold-Export (→ Top-Level teachers-Liste) liest.
 * Roh-urql statt generierter Hooks (Codegen-Hürde im Offline-Build). Liste + Modal wie bei den Klassen.
 */

type Props = { versionUuid: string; teacherUuid: string; name?: string } & ConditionsControlProps;

export const TimetableVersionTeacherConditions = observer(
  ({
    versionUuid,
    teacherUuid,
    name,
    editOpen,
    setEditOpen,
    duplicateOpen,
    setDuplicateOpen,
    duplicateTargets,
  }: Props) => {
    const { t } = useTranslation();
    const store = useTimetableStore();
    const [duplicateItem, setDuplicateItem] = useState<ConstraintsItemType | null>(null);

    const {
      raw,
      resourceType,
      write: writeConstraints,
      refetch,
    } = useVersionConstraints(versionUuid, 'teacher', teacherUuid);

    const constraints: TeacherConstraints = useMemo(() => {
      try {
        return raw ? (JSON.parse(raw) as TeacherConstraints) : {};
      } catch {
        return {};
      }
    }, [raw]);

    const items = useTeacherConstraints(constraints);

    const write = async (targetUuid: string, blob: Record<string, unknown>) => {
      const { error } = await writeConstraints(targetUuid, blob);
      return !error;
    };

    const {
      loading: duplicating,
      duplicateAll,
      duplicateFields,
    } = useConditionsDuplication({
      versionUuid,
      resourceType,
      write,
    });

    async function handleSave(blob: TeacherConstraints): Promise<boolean> {
      if (store.readonly) return false;
      const { error } = await writeConstraints(teacherUuid, blob);
      if (error) {
        showUserErrorToast({ error });
        return false;
      }
      showSuccessSaveToast();
      refetch({ requestPolicy: 'network-only' });
      return true;
    }

    function handleDelete(item: ConstraintsItemType) {
      if (store.readonly) return;
      const descriptor = teacherConstraintFields.find((d) => d.field === item.type);
      const next: TeacherConstraints = { ...constraints };
      delete next[item.type as keyof TeacherConstraints];
      if (descriptor?.priorityField) delete next[descriptor.priorityField];
      handleSave(next);
    }

    async function handleDuplicateAll(targetUuids: string[]) {
      await duplicateAll(constraints as Record<string, unknown>, targetUuids);
      setDuplicateOpen(false);
      showSuccessSaveToast();
    }

    async function handleDuplicateItem(targetUuids: string[]) {
      if (!duplicateItem) return;
      const descriptor = teacherConstraintFields.find((d) => d.field === duplicateItem.type);
      const fields = [duplicateItem.type, ...(descriptor?.priorityField ? [descriptor.priorityField] : [])];
      await duplicateFields(constraints as Record<string, unknown>, fields, targetUuids);
      setDuplicateItem(null);
      showSuccessSaveToast();
    }

    return (
      <>
        {items.length === 0 ? (
          <EmptyState
            icon={<CheckIcon className='bordered' />}
            size='small'
            iconColor='var(--color-success)'
            title={t('timetableVersion.constraint.noConstraints')}
            padding='xl'
          />
        ) : (
          items.map((item) => (
            <ConstraintsItem
              key={item.type}
              item={item}
              onDelete={handleDelete}
              onDuplicate={(value) => setDuplicateItem(value)}
            />
          ))
        )}

        <div className='tks__row px-6 py-4' style={{ justifyContent: 'center' }}>
          <Button hierarchy='secondary' icon={<EditIcon />} onClick={() => setEditOpen(true)} disabled={store.readonly}>
            {t('constraint.edit')}
          </Button>
        </div>

        <Modal
          isOpen={editOpen}
          onRequestClose={() => setEditOpen(false)}
          shouldCloseOnEsc={false}
          shouldCloseOnOverlayClick={false}
          title={t('conditions.for', { for: name ?? '' })}
        >
          <TeacherConditionsForm constraints={constraints} onSubmit={handleSave} onClose={() => setEditOpen(false)} />
        </Modal>

        <DuplicateConditionsModal
          isOpen={duplicateOpen}
          onClose={() => setDuplicateOpen(false)}
          title={t('conditions.duplicateModal.title')}
          hint={t('conditions.duplicateModal.hint', { name: name ?? '' })}
          options={duplicateTargets}
          isLoading={duplicating}
          onConfirm={handleDuplicateAll}
        />

        <DuplicateConditionsModal
          isOpen={!!duplicateItem}
          onClose={() => setDuplicateItem(null)}
          title={t('conditions.duplicateModal.titleSingle')}
          hint={t('conditions.duplicateModal.hintSingle', { name: name ?? '' })}
          options={duplicateTargets}
          isLoading={duplicating}
          onConfirm={handleDuplicateItem}
        />
      </>
    );
  },
);
